import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { plansApi } from '@app/api/organization';
import type { ServicePlan } from '@app/types';
import { Field, Modal, ModalActions, Select, useToast } from '@app/components/ui';
import { shortDate, time12 } from '@app/lib/format';
import { logger } from '@app/utils/logger';

/**
 * Create → New setlist. Picks an upcoming service of the active team and opens
 * it on the Roster in build mode. A setlist the admin builds is an
 * "admin predefined" one, so a service still on "leader builds" gets that
 * per-service override first (the same update the Roster's mode Select makes).
 */
export function NewSetlistModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const { selectedGroup } = useGroup();
  const navigate = useNavigate();
  const toast = useToast();
  const churchId = user?.churchId;
  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [planId, setPlanId] = useState<string>('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !churchId) return;
    setPlanId('');
    plansApi
      .listUpcomingAll(churchId)
      .then((all) =>
        setPlans(
          all
            .filter((p) => !selectedGroup || p.groupId === selectedGroup.id)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
        ),
      )
      .catch((e) => {
        logger.error('Failed to load upcoming services', e);
        setPlans([]);
      });
  }, [open, churchId, selectedGroup]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!planId || !churchId) {
      toast('Choose a service first', 'error');
      return;
    }
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;
    setBusy(true);
    try {
      const mode = plan.setlistModeOverride || selectedGroup?.setlistMode || 'leader_builds';
      if (mode !== 'admin_predefined') {
        await plansApi.update(churchId, plan.id, { setlistModeOverride: 'admin_predefined' });
      }
      onClose();
      navigate(`/admin/roster?plan=${plan.id}${plan.isSetlistPublished ? '&tab=setlist' : '&build=1'}`);
    } catch (err) {
      logger.error('Failed to start setlist', err);
      toast("Couldn't start the setlist. Try again.", 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New setlist"
      subtitle={`Pick an upcoming ${selectedGroup?.name ?? ''} service. You'll add songs from its roster page.`}
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Service" htmlFor="new-setlist-service">
          <Select
            id="new-setlist-service"
            value={planId}
            onChange={setPlanId}
            placeholder={plans.length ? 'Choose a service' : 'No upcoming services'}
            options={plans.map((p) => ({
              value: p.id,
              label: `${p.title} · ${shortDate(p.date)}`,
              sub: `${time12(p.date)} · ${p.items.length ? `already has ${p.items.length} songs` : 'no setlist yet'}`,
            }))}
          />
        </Field>
        <ModalActions onCancel={onClose} confirmLabel="Start setlist" busy={busy} />
      </form>
    </Modal>
  );
}
